import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { MoreHorizontal } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../UI/BaseDropdown/dropdown-base'
import { TabButton } from './TabButton'
import { TabConfig, BaseTabsProps } from './types'

// Re-export for backwards compatibility
export type ResponsiveTabConfig<T> = TabConfig<T>

// Minimum width for the overflow button
const OVERFLOW_BUTTON_WIDTH = 48

export function ResponsiveTabs<T>({
  tabs,
  selectedTab,
  setSelectedTab,
  className = '',
  variant = 'underline',
}: BaseTabsProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null)
  const measureContainerRef = useRef<HTMLDivElement>(null)
  const [visibleCount, setVisibleCount] = useState(tabs.length)
  const [tabWidths, setTabWidths] = useState<number[]>([])
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isMeasured, setIsMeasured] = useState(false)

  // Measure all tab widths once on mount using the hidden measurement container
  useEffect(() => {
    const measureTabs = () => {
      if (!measureContainerRef.current) return

      const buttons = measureContainerRef.current.querySelectorAll('button')
      const widths: number[] = []

      buttons.forEach((btn) => {
        widths.push(btn.getBoundingClientRect().width)
      })

      if (widths.length === tabs.length) {
        setTabWidths(widths)
        setIsMeasured(true)
      }
    }

    // Measure after a short delay to ensure tabs are rendered
    const timer = setTimeout(measureTabs, 50)
    return () => clearTimeout(timer)
  }, [tabs])

  // Calculate visible tabs based on container width
  const calculateVisibleTabs = useCallback(() => {
    if (!containerRef.current || tabWidths.length === 0) return

    // The pills variant has p-1 padding (4px each side) and gap-1
    // between items (4px). Account for both so the overflow button
    // kicks in before content visually clips into the container's
    // padding.
    const TAB_GAP = variant === 'pills' ? 4 : 0
    const CONTAINER_PADDING_X = variant === 'pills' ? 8 : 0

    const containerWidth = containerRef.current.getBoundingClientRect().width
    const availableWidth = containerWidth - CONTAINER_PADDING_X

    // First pass: check if all tabs fit (with gaps between them).
    let tempTotal = 0
    for (let i = 0; i < tabWidths.length; i++) {
      tempTotal += tabWidths[i]
      if (i > 0) tempTotal += TAB_GAP
    }

    if (tempTotal <= availableWidth) {
      setVisibleCount(tabs.length)
      return
    }

    // Not all tabs fit, need overflow button. Reserve room for it AND
    // for the gap between the last visible tab and the overflow.
    const reservedWidth = availableWidth - OVERFLOW_BUTTON_WIDTH - TAB_GAP

    let totalWidth = 0
    let count = 0
    for (let i = 0; i < tabWidths.length; i++) {
      const gapAddition = i > 0 ? TAB_GAP : 0
      if (totalWidth + gapAddition + tabWidths[i] <= reservedWidth) {
        totalWidth += gapAddition + tabWidths[i]
        count++
      } else {
        break
      }
    }

    // Ensure at least one tab is visible
    setVisibleCount(Math.max(1, count))
  }, [tabWidths, tabs.length, variant])

  // Recalculate on resize
  useEffect(() => {
    if (tabWidths.length === 0) return

    calculateVisibleTabs()

    const resizeObserver = new ResizeObserver(() => {
      calculateVisibleTabs()
    })

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current)
    }

    return () => resizeObserver.disconnect()
  }, [calculateVisibleTabs, tabWidths])

  const visibleTabs = useMemo(
    () => tabs.slice(0, visibleCount),
    [tabs, visibleCount]
  )
  const hiddenTabs = useMemo(
    () => tabs.slice(visibleCount),
    [tabs, visibleCount]
  )
  const hasHiddenTabs = hiddenTabs.length > 0

  // Check if selected tab is in the hidden tabs
  const selectedInHidden = useMemo(
    () => hiddenTabs.some((tab) => tab.key === selectedTab),
    [hiddenTabs, selectedTab]
  )

  const renderTabs = (tabsToRender: TabConfig<T>[], isInteractive: boolean) =>
    tabsToRender.map(({ key, title, badge }) => (
      <TabButton
        key={isInteractive ? String(key) : `measure-${String(key)}`}
        title={title}
        badge={badge}
        isActive={isInteractive && selectedTab === key}
        onClick={() => isInteractive && setSelectedTab(key)}
        flexMode="content"
        variant={variant}
      />
    ))

  return (
    <>
      {/* Hidden measurement container - positioned off-screen */}
      <div
        ref={measureContainerRef}
        className="flex absolute -left-[9999px] whitespace-nowrap"
        aria-hidden="true"
        style={{ visibility: 'hidden', pointerEvents: 'none' }}
      >
        {renderTabs(tabs, false)}
      </div>

      {/* Visible tabs container */}
      <div
        ref={containerRef}
        className={`flex w-full ${
          variant === 'pills' ? 'gap-1 bg-gray-100 rounded-md p-1' : ''
        } ${className}`}
      >
        {/* Only render visible tabs after measurement */}
        {isMeasured && renderTabs(visibleTabs, true)}

        {/* Show all tabs before measurement completes */}
        {!isMeasured && renderTabs(tabs, true)}

        {/* Overflow dropdown */}
        {isMeasured && hasHiddenTabs && (
          <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
            <DropdownMenuTrigger asChild>
              <button
                className={
                  variant === 'pills'
                    ? `flex-shrink-0 inline-flex items-center justify-center px-3 h-9 rounded-md text-base text-center relative transition-colors duration-200 ${
                        selectedInHidden
                          ? 'text-fg font-medium bg-white shadow-[0_1px_2px_0_rgba(16,24,40,0.05)]'
                          : 'text-fg-secondary font-medium hover:text-fg'
                      }`
                    : `flex-shrink-0 p-3 text-base text-center relative transition-colors duration-200 ${
                        selectedInHidden
                          ? 'text-fg font-semibold border-b-2 border-green-700'
                          : 'text-fg font-light hover:text-fg'
                      }`
                }
                aria-label="More tabs"
              >
                <MoreHorizontal className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-[176px] p-2 flex flex-col gap-2 bg-white border border-line-focus shadow-[0px_4px_4px_rgba(5,70,40,0.15)] rounded-lg"
            >
              {hiddenTabs.map(({ key, title, icon: Icon }) => {
                const isActive = selectedTab === key
                return (
                  <DropdownMenuItem
                    key={String(key)}
                    onClick={() => {
                      setSelectedTab(key)
                      setIsDropdownOpen(false)
                    }}
                    className={`
                      flex items-center gap-2 p-1 h-[28px] rounded cursor-pointer
                      ${isActive ? 'font-medium' : 'font-normal'}
                      text-fg-dark text-sm leading-5
                      hover:bg-sage-lt
                    `}
                  >
                    {Icon && (
                      <Icon size={20} className="text-gray-500 flex-shrink-0" />
                    )}
                    <span>{title}</span>
                  </DropdownMenuItem>
                )
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </>
  )
}
